<script setup>
import { computed, onActivated, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import Avatar from 'next/avatar/Avatar.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const showDeletePopup = ref(false);
const selectedInbox = ref({});
const searchQuery = ref('');

const inboxes = useMapGetter('inboxes/getInboxes');

onActivated(() => {
  store.dispatch('inboxes/get');
});

// Atualiza o status online/offline das caixas periodicamente.
let onlinePollTimer = null;
onMounted(() => {
  onlinePollTimer = setInterval(() => {
    store.dispatch('inboxes/get');
  }, 20000);
});
onUnmounted(() => {
  if (onlinePollTimer) clearInterval(onlinePollTimer);
});

const inboxesList = computed(() => {
  return inboxes.value?.slice().sort((a, b) => a.name.localeCompare(b.name));
});

const filteredInboxesList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return inboxesList.value;
  return picoSearch(inboxesList.value, query, ['name', 'channel_type']);
});

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

// Indica se a caixa está online (capaz de enviar/receber mensagens).
// Usa o campo "online" do backend quando disponível (sessão real do WuConnect).
const inboxOnline = inbox => {
  if (inbox.online !== undefined && inbox.online !== null) return inbox.online;
  if (inbox.reauthorization_required) return false;
  if (inbox.channel_type === 'Channel::Whatsapp' && inbox.provider === 'wuzapi') {
    // wuzapi: só fica "online" depois do pareamento (telefone real sincronizado)
    return !/^wuzapi\d+$/.test(inbox.phone_number || '');
  }
  if (inbox.channel_type === 'Channel::Whatsapp') {
    return !!(inbox.phone_number && !inbox.reauthorization_required);
  }
  if (inbox.channel_type === 'Channel::Email') {
    return !!(inbox.imap_address && inbox.smtp_address);
  }
  return true;
};

const channelColorClass = inbox => {
  switch (inbox.channel_type) {
    case 'Channel::WebWidget':
      return 'cw-inbox-icon-website';
    case 'Channel::Email':
      return 'cw-inbox-icon-email';
    case 'Channel::Api':
      return 'cw-inbox-icon-api';
    case 'Channel::Sms':
    case 'Channel::TwilioSms':
      return 'cw-inbox-icon-sms';
    case 'Channel::Line':
      return 'cw-inbox-icon-line';
    case 'Channel::FacebookPage':
      return 'cw-inbox-icon-facebook';
    case 'Channel::Instagram':
      return 'cw-inbox-icon-instagram';
    case 'Channel::Tiktok':
      return 'cw-inbox-icon-tiktok';
    case 'Channel::Telegram':
      return 'cw-inbox-icon-telegram';
    case 'Channel::Whatsapp':
      return 'cw-inbox-icon-whatsapp';
    default:
      return 'cw-inbox-icon-default';
  }
};

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);

const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);

const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

const deleteInbox = async ({ id }) => {
  try {
    await store.dispatch('inboxes/delete', id);
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const confirmDeletion = () => {
  deleteInbox(selectedInbox.value);
  closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};
</script>

<template>
  <SettingsLayout
    :no-records-found="!inboxesList.length"
    :no-records-message="$t('INBOX_MGMT.LIST.404')"
    :is-loading="uiFlags.isFetching"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INBOX_MGMT.HEADER')"
        :description="$t('INBOX_MGMT.DESCRIPTION')"
        :link-text="$t('INBOX_MGMT.LEARN_MORE')"
        :search-placeholder="$t('INBOX_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="inboxes"
      >
        <template v-if="inboxesList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('INBOX_MGMT.COUNT', { n: inboxesList.length }) }}
          </span>
        </template>
        <template #actions>
          <router-link v-if="isAdmin" :to="{ name: 'settings_inbox_new' }">
            <Button :label="$t('SETTINGS.INBOXES.NEW_INBOX')" size="sm" />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <span
        v-if="!filteredInboxesList.length && searchQuery"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.NO_RESULTS') }}
      </span>
      <div v-else class="divide-y divide-n-weak border-t border-n-weak">
        <div
          v-for="inbox in filteredInboxesList"
          :key="inbox.id"
          class="flex justify-between flex-row items-start gap-4 py-4"
        >
          <div class="flex items-center gap-4">
            <div
              v-if="inbox.avatar_url"
              class="bg-n-alpha-3 rounded-xl size-10 ring ring-n-solid-1 border border-n-strong shadow-sm grid place-items-center"
            >
              <Avatar
                :src="inbox.avatar_url"
                :name="inbox.name"
                :size="24"
                rounded-full
              />
            </div>
            <div
              v-else
              class="size-10 justify-center bg-n-alpha-3 rounded-xl ring ring-n-solid-1 border border-n-strong shadow-sm grid place-items-center"
            >
              <ChannelIcon
                use-brand-icon
                class="size-6"
                :class="channelColorClass(inbox)"
                :inbox="inbox"
              />
            </div>
            <div class="flex flex-col items-start gap-1">
              <span class="flex items-center gap-2 text-heading-3 text-n-slate-12 capitalize">
                <span
                  class="inline-block size-2.5 rounded-full"
                  :class="
                    inboxOnline(inbox)
                      ? 'bg-n-teal-9'
                      : 'bg-n-ruby-9'
                  "
                  :title="
                    inboxOnline(inbox)
                      ? $t('INBOX_MGMT.ONLINE')
                      : $t('INBOX_MGMT.OFFLINE')
                  "
                />
                {{ inbox.name }}
              </span>
              <ChannelName
                :channel-type="inbox.channel_type"
                :medium="inbox.medium"
                :voice-enabled="inbox.voice_enabled"
                class="text-body-main text-n-slate-11"
              />
            </div>
          </div>
          <div class="flex gap-3 justify-end">
            <router-link
              :to="{
                name: 'settings_inbox_show',
                params: { inboxId: inbox.id },
              }"
            >
              <Button
                v-if="isAdmin"
                v-tooltip.top="$t('INBOX_MGMT.SETTINGS')"
                icon="i-woot-settings"
                slate
                sm
              />
            </router-link>
            <Button
              v-if="isAdmin"
              v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
              icon="i-woot-bin"
              slate
              sm
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="openDelete(inbox)"
            />
          </div>
        </div>
      </div>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>

<style>
/* Cores dos ícones de caixa de entrada por canal (fallback para os canais sem
   logo de marca; os logos completos já possuem cor própria) */
.cw-inbox-icon-website { color: #2563eb; }
.cw-inbox-icon-email { color: #7c3aed; }
.cw-inbox-icon-api { color: #64748b; }
.cw-inbox-icon-sms { color: #f97316; }
.cw-inbox-icon-line { color: #06b6d4; }
.cw-inbox-icon-facebook { color: #1877f2; }
.cw-inbox-icon-instagram { color: #e1306c; }
.cw-inbox-icon-tiktok { color: #0f172a; }
.cw-inbox-icon-telegram { color: #229ed9; }
.cw-inbox-icon-whatsapp { color: #25d366; }
.cw-inbox-icon-default { color: #64748b; }
</style>
